// audioToWav — convert any browser-decodable audio (MediaRecorder webm/ogg, mp3,
// wav, m4a) into a small mono 16-bit PCM WAV. Twilio <Play> only supports
// mp3/wav, and browsers record webm/ogg, so voicemail drops are normalised here.
// 8 kHz mono is phone-grade and ~16 KB/s, so 60 s stays under the 1 MB cap.

export const VOICEMAIL_MAX_SECONDS = 60;
export const VOICEMAIL_MAX_BYTES = 1_000_000;

/**
 * @param {Blob} blob - Recorded or uploaded audio.
 * @param {{sampleRate?: number}} [opts]
 * @returns {Promise<{blob: Blob, durationSec: number}>} WAV blob and its length in seconds.
 * @throws {Error} When the audio can't be decoded or is longer than VOICEMAIL_MAX_SECONDS.
 */
export async function blobToWav(blob, { sampleRate = 8000 } = {}) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) throw new Error('This browser cannot process audio');
  const ctx = new Ctx();
  try {
    const decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
    if (decoded.duration > VOICEMAIL_MAX_SECONDS + 0.5) {
      throw new Error(`Voicemail drops can be at most ${VOICEMAIL_MAX_SECONDS} seconds`);
    }
    const frames = Math.max(1, Math.ceil(decoded.duration * sampleRate));
    const offline = new OfflineAudioContext(1, frames, sampleRate);
    const src = offline.createBufferSource();
    src.buffer = decoded;
    src.connect(offline.destination);
    src.start();
    const rendered = await offline.startRendering();
    return { blob: encodeWav(rendered.getChannelData(0), sampleRate), durationSec: Math.round(decoded.duration) };
  } finally {
    ctx.close?.();
  }
}

/**
 * @param {Float32Array} samples
 * @param {number} sampleRate
 * @returns {Blob} audio/wav
 */
export function encodeWav(samples, sampleRate) {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buf);
  const str = (off, s) => { for (let i = 0; i < s.length; i += 1) view.setUint8(off + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  str(36, 'data'); view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i += 1) {
    const v = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Blob([buf], { type: 'audio/wav' });
}

/**
 * @param {Blob} blob
 * @returns {Promise<string>} data: URL.
 */
export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read audio'));
    r.readAsDataURL(blob);
  });
}
